import React, { useState, useEffect } from "react";
import { getLocalizedString, dateLocale } from "@icarius-localization/strings";
import { CloseIcon } from "@icarius-icons";
import {
    DialogContent,
    DialogActions,
    DialogTitle,
    Dialog,
    TextField,
    Grid,
    MenuItem,
    Select,
    InputLabel,
    FormControl
} from "@material-ui/core";
import { MuiPickersUtilsProvider, DatePicker } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { formatDate, formatDateYYYYMM, createDateFromDDMMYYYY, createDateFromYYYYMM, getDateIsValid } from "@icarius-utils/date";
import DialogTitleDivider from "@icarius-common/dialogTitleDivider";
import ButtonDialogAction from "@icarius-common/buttonDialogAction";
import DialogTransition from "@icarius-common/dialogTransition";
import PaperDraggable from "@icarius-common/paperDraggable";
import TextFieldMultiline from "@icarius-common/textFieldMultiline";
import CustomNumberFormat from '@icarius-common/dynamicInputs/customNumberFormat';

const EMPTY_PARAMETER = {
    paramNum: "",
    name: "",
    required: "N",
    paramType: "",
    listType: "",
    sql: "",
    value: "",
};

const avoidDecimalSeparator = (event) => {
    if (event.charCode === 46 || event.charCode === 44 || event.charCode === 42 || event.charCode === 45 || event.charCode === 43 || event.charCode === 69 || event.charCode === 101) {
        event.preventDefault();
    }
};

const ParameterFormDialog = (props) => {

    const { open, handleClose, handleSave, initialData, paramNumToUse, requiredTypes, listTypes, paramTypes } = props;
    const [formData, setFormData] = useState(EMPTY_PARAMETER);

    useEffect(() => {
        if (open) {
            setFormData(initialData
                ? { ...EMPTY_PARAMETER, ...initialData }
                : { ...EMPTY_PARAMETER, paramNum: paramNumToUse });
        }
    }, [open, initialData, paramNumToUse]);

    const onChange = (e, field) => {
        const value = e.target.value;

        setFormData((prev) => ({
            ...prev,
            [field]: value,
            ...(field === "paramType" ? { value: "" } : {}),
        }));
    };

    const onChangeDate = (date) => {
        setFormData((prev) => ({
            ...prev,
            value: date && getDateIsValid(date)
                ? (prev.paramType === "P" ? formatDateYYYYMM(date) : formatDate(date))
                : "",
        }));
    };

    const getDateValue = () => {
        if (!formData.value) return null;

        const date = formData.paramType === "P"
            ? createDateFromYYYYMM(formData.value)
            : createDateFromDDMMYYYY(formData.value);

        return getDateIsValid(date) ? date : null;
    };

    const renderTestValueField = () => {
        const commonProps = {
            fullWidth: true,
            label: getLocalizedString("testValueForParameter"),
            margin: "dense",
            required: formData.required === "Y",
        };

        switch (formData.paramType) {
            case "N": // numérico entero
                return (
                    <TextField
                        {...commonProps}
                        value={formData.value}
                        onChange={(e) => onChange(e, "value")}
                        InputProps={{ inputComponent: CustomNumberFormat }}
                        inputProps={{ min: "1", step: "1", onKeyPress: avoidDecimalSeparator }}
                    />
                );
            case "M": // numérico con decimales
                return (
                    <TextField
                        {...commonProps}
                        value={formData.value}
                        onChange={(e) => onChange(e, "value")}
                        InputProps={{ inputComponent: CustomNumberFormat }}
                        inputProps={{ min: "1", step: "0.1", decimals: 2 }}
                    />
                );
            case "F": // fecha
            case "P": // período
                return (
                    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
                        <DatePicker
                            {...commonProps}
                            clearable
                            clearLabel={getLocalizedString("clear")}
                            cancelLabel={getLocalizedString("cancel")}
                            okLabel={getLocalizedString("ok")}
                            invalidDateMessage=""
                            format={formData.paramType === "P" ? "yyyy/MM" : "dd/MM/yyyy"}
                            views={formData.paramType === "P" ? ["month", "year"] : undefined}
                            value={getDateValue()}
                            onChange={onChangeDate}
                        />
                    </MuiPickersUtilsProvider>
                );
            default: // alfanumérico
                return (
                    <TextField
                        {...commonProps}
                        value={formData.value}
                        onChange={(e) => onChange(e, "value")}
                        inputProps={{ maxLength: 254 }}
                    />
                );
        }
    };

    const isAcceptDisabled = !formData.name || !formData.paramType || !formData.listType || !formData.required || (formData.required === "Y" && !formData.value);

    const onAccept = () => {
        handleSave({
            ...formData,
            paramNum: parseInt(formData.paramNum) || paramNumToUse,
        });
    };

    const title = initialData
        ? getLocalizedString("editQueryDefinitionParameter")
        : getLocalizedString("createQueryDefinitionParameter");

    return (
        <Dialog
            TransitionComponent={DialogTransition}
            PaperComponent={PaperDraggable}
            open={open}
            scroll={"paper"}
            maxWidth={"sm"}
            fullWidth
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {title}
                <DialogTitleDivider />
            </DialogTitle>
            <CloseIcon className={"dialog-close-icon icon"} onClick={handleClose} />
            <DialogContent style={{ paddingTop: 10 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={8}>
                        <TextField
                            required
                            fullWidth
                            label={getLocalizedString("parameterName")}
                            value={formData.name}
                            onChange={(e) => onChange(e, "name")}
                            margin="dense"
                            inputProps={{ maxLength: 254 }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label={getLocalizedString("parameterOrderNumber")}
                            InputProps={{ inputComponent: CustomNumberFormat }}
                            value={formData.paramNum}
                            onChange={(e) => onChange(e, "paramNum")}
                            margin="dense"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl required fullWidth margin="dense">
                            <InputLabel>{getLocalizedString("parameterType")}</InputLabel>
                            <Select
                                value={formData.paramType || ""}
                                onChange={(e) => onChange(e, "paramType")}
                                labelId={`label-paramType`}
                                id={`select-paramType`}
                                margin={"none"}
                            >
                                {paramTypes?.map((item) => {
                                    return (
                                        <MenuItem key={item.key} value={item.key}>
                                            {item.value}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl required fullWidth margin="dense">
                            <InputLabel>{getLocalizedString("listSelectionType")}</InputLabel>
                            <Select
                                value={formData.listType}
                                onChange={(e) => onChange(e, "listType")}
                                labelId={`label-listType`}
                                id={`select-listType`}
                                margin={"none"}
                            >
                                {listTypes?.map((item) => {
                                    return (
                                        <MenuItem key={item.key} value={item.key}>
                                            {item.value}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl required fullWidth margin="dense">
                            <InputLabel>{getLocalizedString("requiredParameter")}</InputLabel>
                            <Select
                                value={formData.required}
                                onChange={(e) => onChange(e, "required")}
                                labelId={`label-required`}
                                id={`select-required`}
                                margin={"none"}
                            >
                                {requiredTypes?.map((item) => {
                                    return (
                                        <MenuItem key={item.key} value={item.key}>
                                            {item.value}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        {renderTestValueField()}
                    </Grid>
                    <Grid item xs={12}>
                        <TextFieldMultiline
                            fullWidth
                            label={getLocalizedString("sqlToGetValueListForParameter")}
                            rows={10}
                            margin={"dense"}
                            value={formData.sql}
                            onChange={(e) => onChange(e, "sql")}
                            inputProps={{ spellCheck: 'false' }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <ButtonDialogAction onClick={handleClose} text={getLocalizedString("cancel")} />
                <ButtonDialogAction disabled={isAcceptDisabled} onClick={onAccept} isAccept text={getLocalizedString("accept")} />
            </DialogActions>
        </Dialog>
    );
};

export default ParameterFormDialog;
